import { useState, type FormEvent, type ReactNode } from 'react';
import bcrypt from 'bcryptjs';
import { BrandMark } from '../components/BrandMark';
import { Toast } from '../components/Toast';
import { GoogleButton, disableGoogleAutoSelect } from './GoogleButton';

const TOKEN_KEY = 'budget.token';
const DEMO_FLAG_KEY = 'budget.demo';
const HASH = import.meta.env.VITE_PASSWORD_HASH as string | undefined;
const DEMO_HASH = import.meta.env.VITE_DEMO_PASSWORD_HASH as string | undefined;
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

// TEMPORARY: hide the "Use password instead" affordance and its form. Flip
// to true (or remove the guard usage in the JSX) to restore.
const SHOW_PASSWORD_FALLBACK = false;

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return readToken();
}

export function isDemoMode(): boolean {
  try {
    return localStorage.getItem(DEMO_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

export function lock(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(DEMO_FLAG_KEY);
  } catch {
    /* ignore */
  }
  disableGoogleAutoSelect();
}

interface PasswordGateProps {
  children: ReactNode;
}

export function PasswordGate({ children }: PasswordGateProps) {
  const [unlocked, setUnlocked] = useState(() => readToken() !== null);
  const [showDemo, setShowDemo] = useState(false);
  const [input, setInput] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (unlocked) return <>{children}</>;

  if (!HASH || !GOOGLE_CLIENT_ID) {
    return (
      <div className="auth-container">
        <div className="auth-form glow-brand text-center space-y-3">
          <div className="brand justify-center">
            <BrandMark className="h-7 mb-1 w-auto" />
          </div>
          <h1 className="text-lg font-semibold">Setup needed</h1>
          <p className="text-sm text-fg-secondary">
            Missing environment variables:{' '}
            {!GOOGLE_CLIENT_ID && (
              <code className="px-1.5 py-0.5 rounded bg-subtle text-fg">
                VITE_GOOGLE_CLIENT_ID
              </code>
            )}
            {!GOOGLE_CLIENT_ID && !HASH && ' and '}
            {!HASH && (
              <code className="px-1.5 py-0.5 rounded bg-subtle text-fg">
                VITE_PASSWORD_HASH
              </code>
            )}
            .
          </p>
        </div>
      </div>
    );
  }

  const onGoogleAuthCode = async (code: string) => {
    setError(null);
    try {
      // Send the OAuth 2.0 authorization code to the server, which
      // exchanges it for id/access/refresh tokens, verifies identity,
      // persists the encrypted refresh token (for calendar reads), and
      // returns our long-lived session token. Only the session token is
      // stored client-side.
      const res = await fetch('/api/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      if (!res.ok) {
        // Surface the server's typed error code as a friendly message. The
        // body shape is { error: string }; we map known codes to copy a
        // user can act on. Anything unrecognized falls through to a
        // generic message with no raw status/JSON leaking into the UI.
        const body = await res.text().catch(() => '');
        let serverCode = '';
        try {
          serverCode = (JSON.parse(body) as { error?: string }).error ?? '';
        } catch {
          // body wasn't JSON — leave serverCode empty.
        }
        if (serverCode === 'email not allowlisted') {
          throw new Error(
            "Sorry, this Google account isn't on the approved list. Please try another.",
          );
        }
        if (res.status === 401 || res.status === 403) {
          throw new Error("We couldn't sign you in with that Google account.");
        }
        throw new Error("Sign-in failed. Please try again.");
      }
      const data = (await res.json()) as { sessionToken: string };
      localStorage.setItem(TOKEN_KEY, `session:${data.sessionToken}`);
      localStorage.removeItem(DEMO_FLAG_KEY);
      setUnlocked(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    }
  };

  const onPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setChecking(true);
    setError(null);
    try {
      const realOk = await bcrypt.compare(input, HASH);
      const demoOk =
        !realOk && DEMO_HASH ? await bcrypt.compare(input, DEMO_HASH) : false;
      if (realOk) {
        localStorage.setItem(TOKEN_KEY, `password:${input}`);
        localStorage.removeItem(DEMO_FLAG_KEY);
        setUnlocked(true);
      } else if (demoOk) {
        localStorage.setItem(TOKEN_KEY, `demo:${input}`);
        localStorage.setItem(DEMO_FLAG_KEY, '1');
        setUnlocked(true);
      } else {
        setError('Incorrect password');
      }
    } catch {
      setError('Something went wrong');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="auth-container relative">
      <div className="auth-form glow-brand space-y-5">
        <div className="text-center space-y-2">
          <div className="brand justify-center text-lg">
            <BrandMark className="h-7 mb-1 w-auto" />
          </div>
        </div>

        <GoogleButton
          clientId={GOOGLE_CLIENT_ID}
          onAuthCode={(c) => void onGoogleAuthCode(c)}
          onError={() => setError('Google sign-in failed to load')}
        />

        {/* TEMPORARY: password fallback hidden. Flip SHOW_PASSWORD_FALLBACK
            back to true (or delete the guard) to restore the affordance. */}
        {SHOW_PASSWORD_FALLBACK &&
          (!showDemo ? (
            <button
              type="button"
              onClick={() => setShowDemo(true)}
              className="block mx-auto text-xs text-fg-secondary hover:text-fg underline"
            >
              Use password instead
            </button>
          ) : (
            <form onSubmit={onPasswordSubmit} className="space-y-3">
              <input
                type="password"
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="input"
                placeholder="Password"
              />
              <button
                type="submit"
                disabled={checking || !input}
                className="btn btn-primary w-full"
              >
                {checking ? 'Checking…' : 'Unlock'}
              </button>
            </form>
          ))}

      </div>
      {error && (
        <Toast
          tone="error"
          duration={20000}
          message={error}
          onClose={() => setError(null)}
        />
      )}

      <a
        href="/privacy"
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs text-fg-tertiary hover:text-fg-secondary"
      >
        Privacy policy
      </a>
    </div>
  );
}
